import React, { useState, useEffect, useRef } from 'react';

const PartnerSuccess = () => {
  const [happyClients, setHappyClients] = useState(0);
  const [projects, setProjects] = useState(0);
  const [runningProjects, setRunningProjects] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const componentRef = useRef(null);

  const baseStyles = {
    container: {
      display: 'flex',
      alignItems: 'center',
      gap: '50px',
      padding: '40px 20px',
      maxWidth: '1100px',
      margin: '0 auto',
      opacity: 0,
      transform: 'translateY(20px)',
      transition: 'opacity 0.6s ease-out, transform 0.6s ease-out',
      position: 'relative',
    },
    imageContainer: {
      flex: '0 0 300px',
      position: 'relative',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    image: {
      width: 'auto',
      height: '330px',
      maxWidth: '100%',
      objectFit: 'contain',
    },
    content: {
      flex: '1',
      minWidth: 0,
    },
    heading: {
      fontSize: '40px',
      fontFamily: 'Oswald, sans-serif',
      color: '#410684',
      fontWeight: '600',
      lineHeight: '40px',
      marginBottom: '15px'
    },
    paragraph: {
      color: '#414042',
      fontSize: '16px',
      lineHeight: '24px',
      fontFamily: 'Inter, sans-serif',
      fontWeight: '400',
      maxWidth: '600px',
    },
    statsContainer: {
      display: 'flex',
      gap: '10px',
      marginTop: '30px',
      flexWrap: 'wrap',
    },
    statItem: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      flex: '1 1 auto',
      minWidth: '140px',
    },
    number: {
      fontSize: 'clamp(24px, 3vw, 36px)',
      fontWeight: 'bold',
      color: '#DC2666',
      marginBottom: '5px',
    },
    label: {
      fontSize: '13px',
      color: '#414042',
      fontFamily: 'Inter, sans-serif',
      fontWeight: '100',
      textTransform: 'uppercase',
      letterSpacing: '1px',
    },
  };

  const getResponsiveStyles = () => {
    const width = window.innerWidth;
    let styles = { ...baseStyles };

    if (width < 576) {
      styles.container = {
        ...styles.container,
        flexDirection: 'column',
        gap: '24px',
        padding: '20px 16px',
      };
      styles.imageContainer = {
        ...styles.imageContainer,
        flex: '0 0 auto',
        width: '100%',
        maxWidth: '100%',
        display: 'flex',
        justifyContent: 'center',
      };
      styles.image = {
        ...styles.image,
        height: 'auto',
        maxHeight: '330px',
        width: 'auto',
        maxWidth: '100%',
      };
      styles.content = {
        ...styles.content,
        textAlign: 'center',
      };
      styles.statsContainer = {
        ...styles.statsContainer,
        justifyContent: 'center',
        gap: '16px',
      };
      styles.statItem = {
        ...styles.statItem,
        alignItems: 'center',
        minWidth: '120px',
      };
    }
    else if (width < 768) {
      styles.container = {
        ...styles.container,
        flexDirection: 'column',
        gap: '32px',
        padding: '30px 24px',
      };
      styles.imageContainer = {
        ...styles.imageContainer,
        flex: '0 0 auto',
        width: '80%',
        maxWidth: '450px',
        display: 'flex',
        justifyContent: 'center',
      };
      styles.image = {
        ...styles.image,
        height: 'auto',
        maxHeight: '330px',
        width: 'auto',
        maxWidth: '100%',
      };
      styles.content = {
        ...styles.content,
        textAlign: 'center',
      };
      styles.statsContainer = {
        ...styles.statsContainer,
        justifyContent: 'center',
      };
      styles.statItem = {
        ...styles.statItem,
        alignItems: 'center',
      };
    }
    else if (width < 992) {
      styles.container = {
        ...styles.container,
        gap: '32px',
        padding: '40px 32px',
      };
      styles.imageContainer = {
        ...styles.imageContainer,
        flex: '0 0 300px',
      };
    }
    else if (width < 1200) {
      styles.container = {
        ...styles.container,
        gap: '40px',
        padding: '40px',
      };
      styles.imageContainer = {
        ...styles.imageContainer,
        flex: '0 0 350px',
      };
    }

    return styles;
  };

  const [styles, setStyles] = useState(getResponsiveStyles());

  useEffect(() => {
    const handleResize = () => {
      setStyles(getResponsiveStyles());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const animateValue = (start, end, duration, setValue) => {
    let startTimestamp = null;
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      setValue(Math.floor(progress * (end - start) + start));
      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };
    window.requestAnimationFrame(step);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            animateValue(0, 100, 2000, setHappyClients);
            animateValue(0, 10, 2000, setProjects);
            animateValue(0, 100, 2000, setRunningProjects);
            
            if (componentRef.current) {
              componentRef.current.style.opacity = '1';
              componentRef.current.style.transform = 'translateY(0)';
            }
            
            setHasAnimated(true);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        threshold: 0.2,
        rootMargin: '0px',
      }
    );

    if (componentRef.current) {
      observer.observe(componentRef.current);
    }

    return () => {
      if (componentRef.current) {
        observer.unobserve(componentRef.current);
      }
    };
  }, [hasAnimated]);

  return (
    <div ref={componentRef} style={styles.container}>
      <div style={styles.imageContainer}>
        <img 
          src="/about-sm.jpg"
          alt="Success Partner"
          style={styles.image}
        />
      </div>
      <div style={styles.content}>
        <h1 style={styles.heading}>
          WE ARE YOUR PARTNER IN SUCCESS
        </h1>
        <p style={styles.paragraph}>
          Ejil Enterprise LLC has partnerships with leading providers of enterprise cloud communications, collaboration, and contact center solutions that are more flexible and cost-effective than legacy on-premises systems. These solutions empower workforces to use unified voice, video meetings, team messaging, digital customer engagement, and integrated contact center solutions for enterprises globally
        </p>
        <div style={styles.statsContainer}>
          <div style={styles.statItem}>
            <div style={styles.number}>{happyClients}%</div>
            <div style={styles.label}>Management and Project leads</div>
          </div>
          <div style={styles.statItem}>
            <div style={styles.number}>{projects}+</div>
            <div style={styles.label}>Products</div>
          </div>
          <div style={styles.statItem}>
            <div style={styles.number}>{runningProjects}%</div>
            <div style={styles.label}>Backup recovery facilities </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerSuccess;